import React, {useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {
  CalculatorIcon,
  CheckCircleIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  PencilAltIcon,
  QuestionMarkCircleIcon, UsersIcon,
  XCircleIcon
} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import RoleSelect from "components/InsightControls/RoleSelect";
import QuizAttemptsChart from "components/Chart/QuizAttemptsChart";
import {truncate} from "services/string";

import type {EducationInsightFilters, InsightFilters} from "types/OperatorInsights";
import type {Role} from "types/Role";
import ExportButton from "../../../components/ExportButton";
import {useOperator} from "../../../context/OperatorContext";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Learning": "/insights/education"}

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function EducationInsights() {
  const {termFor} = useOperator();
  const [filters, setFilters] = useState<EducationInsightFilters>({...defaultFilters, role: null});

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'education', filters],
    ({signal}) => ngage.insights.education(filters, {signal}),
    {keepPreviousData: true}
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectRole(role: Role | null) {
    setFilters({...filters, role: role?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.educationExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-education-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="Learning"
      subTitle='Understand more about how app users are engaging with quizzes'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <RoleSelect id="select-role" name="role" label="Role" onChange={selectRole}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={4}>
          <StatBox
            name={termFor('Quizzes')}
            icon={QuestionMarkCircleIcon}
            stat={data?.summary.total_quizzes.toLocaleString()}
            change={data?.summary.new_quizzes.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of ${termFor('quizzes')} added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name={`${termFor('Quiz')} Attempts`}
            icon={PencilAltIcon}
            stat={data?.summary.total_quiz_attempts.toLocaleString()}
            change={data?.summary.new_quiz_attempts.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of ${termFor('quiz')} attempts started as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name={`${termFor('Quiz')} Completion Rate`}
            icon={CheckCircleIcon}
            stat={toPercentage(data?.summary.quiz_completion_rate)}
            isLoading={isLoading}
            tooltip={`The proportion of ${termFor('quiz')} attempts that were completed between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name={`${termFor('Quiz')} Abandonment Rate`}
            icon={XCircleIcon}
            stat={toPercentage(data?.summary.quiz_abandonment_rate)}
            isLoading={isLoading}
            tooltip={`The proportion of quiz attempts that were abandoned between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name={`Average ${termFor('Quiz')} Score`}
            icon={CalculatorIcon}
            stat={toPercentage(data?.summary.mean_quiz_score_percentage)}
            isLoading={isLoading}
            tooltip={`The mean average score % achieved in ${termFor('quiz')} attempts between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Participants"
            icon={UsersIcon}
            stat={data?.summary.total_quizzers.toLocaleString()}
            change={data?.summary.new_quizzers.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of people that attempted ${termFor('quizzes')} added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name={`Least Popular ${termFor('Quiz')}`}
            icon={ChevronDoubleDownIcon}
            stat={truncate(data?.summary.least_popular_quiz?.title ?? 'N/A', 35)}
            isLoading={isLoading}
            tooltip={`The ${termFor('quiz')} that has the fewest attempts between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name={`Most Popular ${termFor('Quiz')}`}
            icon={ChevronDoubleUpIcon}
            stat={truncate(data?.summary.most_popular_quiz?.title ?? 'N/A', 35)}
            isLoading={isLoading}
            tooltip={`The ${termFor('quiz')} that has the most attempts between ${sinceDateString} and ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">

        <Card
          heading={`${termFor('Quiz')} Attempts`}
          subheading={`Total ${termFor('quiz')} attempts per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <QuizAttemptsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>
      </div>
    </Page>
  );
}

export default EducationInsights;